import { User } from "@auth0/auth0-react";
import { handleParseFlightConfigValue } from "./handleParseFlightConfigValue";

type MultiConfigContext = {
  pageName: string;
  user?: User;
  currentProcessId?: string | null;
  currentGroupId?: string | null;
  currentWorkScheduleId?: string | null;
};

/**
 * Handles multi-config values where different values can be specified for different conditions.
 *
 * Example:
 * ```
 * {
 *   "[email=daniel@raventelemetry.com]": "css for daniel",
 *   "[email=bob@cat.com;page=trends]": "css for bob on trends page",
 *   "default": "default css"
 * }
 * ```
 *
 * @param multiConfigValue Object with condition keys and values
 * @param context The current context (page, user, process, etc.)
 * @returns The first matching value or the default value
 */
export function handleMultiConfigValue<T>(
  multiConfigValue: Record<string, T> | T,
  context: MultiConfigContext
): T {
  // If the value is not an object or is null/undefined, return it as is
  if (
    typeof multiConfigValue !== "object" ||
    multiConfigValue === null ||
    Array.isArray(multiConfigValue)
  ) {
    return multiConfigValue as T;
  }

  // Convert to record to handle the object case
  const configRecord = multiConfigValue as Record<string, T>;

  // Check if any keys match our pattern format
  const hasConfigKeys = Object.keys(configRecord).some(
    key => key.startsWith("[") && key.endsWith("]")
  );

  // If there are no config keys, return the original value
  if (!hasConfigKeys) {
    return multiConfigValue as T;
  }

  // Try to find a matching config key
  for (const [key, value] of Object.entries(configRecord)) {
    // Skip the default key
    if (key === "default") {
      continue;
    }

    const isMatch = handleParseFlightConfigValue(key, context);
    if (isMatch === true) {
      return value;
    }
  }

  // If we have a default value, return it
  if ("default" in configRecord) {
    return configRecord.default;
  }

  // If no match found and no default, return the original value
  return multiConfigValue as T;
}

/**
 * A hook-friendly wrapper for the handleMultiConfigValue function that allows
 * specific config properties to be processed with multi-config rules.
 *
 * @param config The original config object
 * @param propertyPath Path to the property to be processed (e.g., "general.customCss")
 * @param context The current context (page, user, process, etc.)
 * @returns A new config object with the specified property processed according to multi-config rules
 */
export function processConfigProperty<T extends Record<string, any>>(
  config: T,
  propertyPath: string,
  context: MultiConfigContext
): T {
  // Create a copy of the config to avoid mutating the original
  const processedConfig = { ...config };

  // Split the property path into parts
  const parts = propertyPath.split(".");

  // Helper function to get a nested property value
  const getNestedProperty = (obj: any, path: string[]): any => {
    return path.reduce((prev, curr) => prev && prev[curr], obj);
  };

  // Helper function to set a nested property value
  const setNestedProperty = (obj: any, path: string[], value: any): void => {
    const lastKey = path.pop();
    if (!lastKey) {
      return;
    }

    const target = path.reduce((prev, curr) => {
      if (!prev[curr]) {
        prev[curr] = {};
      }
      return prev[curr];
    }, obj);

    target[lastKey] = value;
  };

  // Get the original property value
  const originalValue = getNestedProperty(processedConfig, parts);

  // Process the property value with multi-config rules
  const processedValue = handleMultiConfigValue(originalValue, context);

  // Set the processed value back to the config
  setNestedProperty(processedConfig, [...parts], processedValue);

  return processedConfig;
}
